import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import { blockTypes, contentBlockTypes } from "../config/blockTypes";
import type { PlatformBlockType } from "../types";
import { DialogHeader } from "@/shared/components";

interface SelectBlockFormProps {
  onClose: () => void;
  onSelectBlock: (
    blockType: PlatformBlockType,
    viewType?: "CAROUSEL" | "GRID"
  ) => void;
  title?: string;
}

/**
 * Как в Sprinty: один массив types (карусель, сетка, блоки), второй — contentBlockTypes.
 * Выбрал с viewType → сохраняем viewType, показываем contentBlockTypes. Выбрал с type → сразу блок.
 */
export const SelectBlockForm: React.FC<SelectBlockFormProps> = ({
  onClose,
  onSelectBlock,
  title,
}) => {
  const { t } = useTranslation();
  const [viewType, setViewType] = useState<"CAROUSEL" | "GRID" | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  const listToShow = viewType ? contentBlockTypes : blockTypes;

  const handleNext = () => {
    if (selectedIndex === undefined) return;

    if (viewType) {
      const config = contentBlockTypes[selectedIndex];
      if (config) onSelectBlock(config.type, viewType);
    } else {
      const item = blockTypes[selectedIndex];
      if ("viewType" in item) {
        setViewType(item.viewType);
        setSelectedIndex(undefined);
      } else {
        onSelectBlock(item.type);
      }
    }
  };

  const handleBack = () => {
    setViewType(null);
    setSelectedIndex(undefined);
  };

  return (
    <>
      <DialogHeader
        title={title ?? t("platformBlocks.dialog.newTitle")}
        onClose={onClose}
        actions={
          <>
            {viewType && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                {t("platformBlocks.dialog.back")}
              </Button>
            )}
            <Button
              color="primary"
              variant="contained"
              onClick={handleNext}
              disabled={selectedIndex === undefined}
            >
              {t("platformBlocks.dialog.next")}
            </Button>
          </>
        }
      />
      <DialogContent sx={{ p: 1, flex: 1, overflow: "auto", minHeight: 400 }}>
        <Stack spacing={1}>
          {viewType && (
            <Card elevation={0}>
              <CardHeader
                slotProps={{ title: { variant: "subtitle1" } }}
                title={t("platformBlocks.dialog.selectContent")}
              />
            </Card>
          )}
          {listToShow.map((item, index) => {
            const icon = "icon" in item ? item.icon : null;
            const label = t(item.labelKey);
            const key = "viewType" in item ? item.viewType : item.type;

            return (
              <Card
                key={key}
                elevation={0}
                sx={{
                  backgroundColor:
                    selectedIndex === index ? "primary.main" : undefined,
                }}
              >
                <CardActionArea onClick={() => setSelectedIndex(index)}>
                  <CardHeader avatar={icon ?? undefined} title={label} />
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      </DialogContent>
    </>
  );
};
